/**
 * ChatMessageList — scroll container for the message stream. Auto-scrolls
 * to the bottom on new content unless the user has scrolled up — the
 * "scroll-lock" behaviour every chat UX needs but most get wrong.
 *
 * The trick: we treat the user as "anchored to bottom" if their scroll
 * position is within a small threshold of the viewport bottom. New content
 * during stream then pushes the viewport down. The moment the user scrolls
 * up past the threshold, autoscroll disables until they return manually
 * (or click a "Jump to latest" affordance — Phase 2).
 */

import { useEffect, useRef } from 'preact/hooks'
import { chatMessages } from '../../state/chatStore.js'
import { ChatMessage } from './ChatMessage.jsx'

const ANCHOR_THRESHOLD_PX = 64

/**
 * @param {object} props
 * @param {import('preact').ComponentChildren} [props.empty] — slot rendered when no messages
 */
export function ChatMessageList({ empty }) {
  const containerRef = useRef(null)
  const anchoredRef = useRef(true)
  const messages = chatMessages.value

  // Track scroll position to maintain anchored-to-bottom flag.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    function onScroll() {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      anchoredRef.current = distanceFromBottom < ANCHOR_THRESHOLD_PX
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Auto-scroll on new content if anchored.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    if (!anchoredRef.current) return
    // rAF so the layout has the new content measured before we scroll.
    const handle = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
    return () => cancelAnimationFrame(handle)
  }, [messages])

  if (!messages || messages.length === 0) {
    return (
      <div class="ion-msglist ion-msglist--empty" ref={containerRef}>
        {empty}
      </div>
    )
  }

  // Track the 0-based ordinal among ASSISTANT messages so each assistant
  // turn carries a stable feedback-target handle (matches the backend's
  // target_turn_index). Computed inline to avoid a second pass.
  let assistantSeen = -1

  return (
    <div class="ion-msglist" ref={containerRef} aria-live="polite">
      {messages.map((message) => {
        // An ask_user answer is recorded for backend context but shown by the
        // collapsed question card, not as a duplicate user bubble.
        if (message.role === 'user' && message.meta && message.meta.askUserAnswer) {
          return null
        }
        const assistantIndex =
          message.role === 'assistant' ? (assistantSeen += 1) : undefined
        return (
          <ChatMessage
            key={message.id}
            message={message}
            assistantIndex={assistantIndex}
          />
        )
      })}
    </div>
  )
}
